/**
 * Requires a firmware package to be chosen before a MoTeC ECU can be added to
 * the cart, and adds both line items together.
 *
 * The checkout guard (license-guard.js) stays as the backstop, but telling a
 * customer at checkout that their cart is invalid is a bad way to find out.
 * This moves the requirement to the moment of the decision, on the page where
 * the customer can actually act on it.
 *
 * Compatibility, in order of how much we actually know:
 *
 *   1. "FitsECU:M142" tags - the package states which ECUs it runs on. Taken
 *      from the products' own copy ("only works in MoTeC M142 ECUs",
 *      "Requires purchase of an M142 or M182 ECU", "(M150 only)").
 *   2. An "ECU Model" variant option - the package is sold per ECU model, so
 *      the option values ARE the compatibility list and the matching variant
 *      is selected automatically.
 *   3. Everything else is offered for any M1 ECU. Deliberately loose: nothing
 *      records which ECUs those fit, and guessing would block real sales.
 *
 * To narrow a package, tag it - no code change needed. Tags are preferred over
 * variant options because they are additive and reversible; adding an option to
 * a live product restructures its variants, SKUs and inventory.
 *
 * Exempt, matching license-guard.js so the two rules cannot disagree:
 *   - plug-and-play kits, which already bundle their package
 *   - "M130 GP Lite", an ECU bundled with package 23456 (SKU MOT-M130-23456)
 *   - legacy M-series (M400/M600/M800/M880), which no M1 package fits
 *
 * Storefront-only, like the checkout guard: it covers every path a customer
 * takes but cannot stop a hand-crafted POST. Only a Shopify cart-validation
 * function can do that.
 */
(function () {
  'use strict';

  var CFG = window.RAV_ECU_PICKER || {};
  var ECU_TYPE = CFG.ecuType || 'ECU';
  var VENDOR = new RegExp(CFG.vendorPattern || 'motec', 'i');
  var LEGACY = new RegExp(CFG.legacyPattern || '\\bM(400|600|800|880)\\b', 'i');
  var PKG_URL = CFG.packagesUrl || '/collections/motec-ecu-packages/products.json?limit=250';
  var LABEL = CFG.label || 'Firmware package (required)';
  var HINT = CFG.hint ||
    'A MoTeC ECU ships without firmware. Choose the package for your application ' +
    'and it will be added alongside the ECU.';
  var PLACEHOLDER = CFG.placeholder || 'Select a firmware package…';

  // Kits bundle their own package. So does "M130 GP Lite", whose SKU
  // (MOT-M130-23456) is an ECU part number with a package part number welded
  // on - the title gives no hint, so it has to be named.
  var BUNDLE = new RegExp(CFG.bundlePattern || '\\bkits?\\b|\\bGP Lite\\b', 'i');
  var MODEL = /\bM(\d{3})\b/i;
  var FITS = /^fits\s*ecu\s*:\s*(.+)$/i;
  var WRAP_ID = 'ecu-package-picker';

  /* The two storefront endpoints disagree about price, so never assume:
       /collections/<h>/products.json -> string dollars, "1149.00"
       /products/<handle>.js          -> integer cents,  114900
     Packages come from the first. Treating that string as cents renders a
     $1,149.00 package as $11.49. */
  function money(price) {
    var dollars = typeof price === 'number' ? price / 100 : parseFloat(price);
    if (!isFinite(dollars)) return '';
    return '$' + dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* Same split: products.json exposes option1/option2/option3 on the variant,
     while the .js endpoint exposes an options array. Read both. */
  function optionValue(variant, idx) {
    if (variant.options && variant.options.length) return variant.options[idx];
    return variant['option' + (idx + 1)];
  }

  function modelOf(title) {
    var m = MODEL.exec(title || '');
    return m ? ('M' + m[1]).toUpperCase() : null;
  }

  /* Index of the package's "ECU Model" option, or -1 when it does not have one. */
  function modelOptionIndex(pkg) {
    var opts = pkg.options || [];
    for (var i = 0; i < opts.length; i++) {
      var name = typeof opts[i] === 'string' ? opts[i] : (opts[i] && opts[i].name);
      if (/ecu\s*model/i.test(name || '')) return i;
    }
    return -1;
  }

  /* Compatible ECU models declared by "FitsECU:M142" tags, or null when the
     package makes no claim. Tags are used rather than variant options because
     they are additive and reversible: adding an option to a live product
     restructures its variants, SKUs and inventory, which is a lot of risk to
     take on for a dropdown filter. Nine packages state their compatibility in
     their own copy ("only works in MoTeC M142 ECUs"); those became tags. */
  function fitsModels(pkg) {
    var tags = pkg.tags || [], out = null;
    for (var i = 0; i < tags.length; i++) {
      var m = FITS.exec(String(tags[i]).trim());
      if (m) (out = out || []).push(m[1].trim().toUpperCase());
    }
    return out;
  }

  function variantFor(pkg, model) {
    var fits = fitsModels(pkg);
    if (fits && fits.indexOf(model) < 0) return null;
    var idx = modelOptionIndex(pkg);
    var vs = pkg.variants || [];
    if (idx < 0) return vs[0] || null;
    for (var i = 0; i < vs.length; i++) {
      var val = optionValue(vs[i], idx);
      if (val && String(val).trim().toUpperCase() === model) return vs[i];
    }
    return null; // declares an ECU Model option but not this one -> incompatible
  }

  function injectStyles() {
    if (document.getElementById('ecu-picker-styles')) return;
    var s = document.createElement('style');
    s.id = 'ecu-picker-styles';
    s.textContent =
      '#' + WRAP_ID + '{margin:0 0 1rem}' +
      '#' + WRAP_ID + ' label{display:block;font-weight:600;margin-bottom:.35rem;font-size:.95rem}' +
      '#' + WRAP_ID + ' select{width:100%;padding:.6rem .7rem;border:1px solid currentColor;' +
      'border-radius:.4rem;font:inherit;background:transparent;color:inherit}' +
      '#' + WRAP_ID + ' .ecu-picker-hint{margin:.4rem 0 0;font-size:.85rem;opacity:.75;line-height:1.4}' +
      '#' + WRAP_ID + ' .ecu-picker-error{margin:.4rem 0 0;font-size:.85rem;color:#c0392b}' +
      'button[name="add"][disabled]{opacity:.5;cursor:not-allowed}';
    document.head.appendChild(s);
  }

  function build(product, packages) {
    var form = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!form || document.getElementById(WRAP_ID)) return;
    var button = form.querySelector('button[name="add"]');
    if (!button) return;

    var model = modelOf(product.title);
    var usable = [];
    for (var i = 0; i < packages.length; i++) {
      var v = variantFor(packages[i], model);
      if (v && v.available !== false) usable.push({ pkg: packages[i], variant: v });
    }
    // Never strand the customer: with nothing to offer, leave the page alone
    // and let the checkout guard handle it rather than blocking the sale here.
    if (!usable.length) return;

    usable.sort(function (a, b) { return a.pkg.title.localeCompare(b.pkg.title); });

    injectStyles();
    var wrap = document.createElement('div');
    wrap.id = WRAP_ID;
    var selectId = WRAP_ID + '-select';
    var html = '<label for="' + selectId + '">' + LABEL + '</label>' +
               '<select id="' + selectId + '"><option value="">' + PLACEHOLDER + '</option>';
    for (var j = 0; j < usable.length; j++) {
      var u = usable[j];
      var suffix = modelOptionIndex(u.pkg) >= 0 ? ' – ' + model : '';
      html += '<option value="' + u.variant.id + '">' +
              u.pkg.title.replace(/</g, '&lt;') + suffix +
              ' (' + money(u.variant.price) + ')</option>';
    }
    html += '</select><p class="ecu-picker-hint">' + HINT + '</p>' +
            '<p class="ecu-picker-error" hidden></p>';
    wrap.innerHTML = html;
    button.parentNode.insertBefore(wrap, button);

    var select = wrap.querySelector('select');
    var error = wrap.querySelector('.ecu-picker-error');
    var originalText = button.textContent;

    function sync() {
      var chosen = !!select.value;
      button.disabled = !chosen;
      if (!chosen) error.hidden = true;
    }
    select.addEventListener('change', sync);
    sync();

    /* The theme's <product-form> element binds submit on this same form in its
       constructor, which runs while the document parses - long before this
       deferred script. Listeners on one element fire in registration order and
       preventDefault() does not stop the others, so a listener added here would
       run SECOND, after the theme had already added the bare ECU on its own.
       The result would be the ECU added twice and the package once.
       So intercept in the CAPTURE phase at document level, which runs before
       any listener on the form, and stopPropagation() so the theme's never
       fires. Same approach license-guard.js uses on the checkout button. */
    var addingPackage = false;
    document.addEventListener('submit', function (e) {
      if (e.target !== form || addingPackage) return;

      if (!select.value) {
        e.preventDefault();
        e.stopPropagation();
        error.textContent = 'Choose a firmware package first.';
        error.hidden = false;
        select.focus();
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      var qtyInput = form.querySelector('[name="quantity"]');
      var qty = Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1);

      button.disabled = true;
      button.textContent = 'Adding…';
      /* Add the package here, then hand the ECU back to the theme by
         re-submitting: the theme opens its own mini-cart and renders the
         section updates, which a redirect to /cart would throw away. */
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(select.value), quantity: qty }] })
      }).then(function (r) {
        if (!r.ok) {
          return r.json().then(function (d) {
            throw new Error(d.description || 'Could not add the firmware package.');
          });
        }
        addingPackage = true;
        button.disabled = false;
        button.textContent = originalText;
        if (form.requestSubmit) form.requestSubmit(button);
        else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        // Re-arm for a second add from the same page view.
        setTimeout(function () { addingPackage = false; }, 0);
      }).catch(function (err) {
        button.disabled = false;
        button.textContent = originalText;
        error.textContent = err.message || 'Could not add to cart. Please try again.';
        error.hidden = false;
      });
    }, true);
  }

  function start() {
    var path = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!path) return;
    fetch('/products/' + path[1] + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (product) {
        if (!product) return null;
        if (product.type !== ECU_TYPE) return null;
        if (!VENDOR.test(product.vendor || '')) return null;
        if (BUNDLE.test(product.title || '')) return null;
        if (LEGACY.test(product.title || '')) return null;
        return fetch(PKG_URL, { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (data && data.products && data.products.length) {
              build(product, data.products);
            }
          });
      })
      .catch(function () { /* leave the page as-is; checkout guard still applies */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
